import React, { useState, useEffect } from 'react';
import { useWizard } from '../../context/WizardContext';
import NavigationButtons from '../shared/NavigationButtons';
import ExplanationBox from '../shared/ExplanationBox';
import GuardrailWarning from '../shared/GuardrailWarning';
import GlossaryTerm from '../Glossary/GlossaryTerm';

const Step4_Strategy = () => {
  const { dataA, updateDataRaw, nextStep, logDecision } = useWizard();
  const [selectedStrategy, setSelectedStrategy] = useState(dataA.strategy || '');
  const [strategyDetails, setStrategyDetails] = useState(dataA.strategyDetails || {});
  const [warnings, setWarnings] = useState([]);

  useEffect(() => {
    if (dataA.strategy) {
      setSelectedStrategy(dataA.strategy);
    }
    if (dataA.strategyDetails) {
      setStrategyDetails(dataA.strategyDetails);
    }
  }, [dataA.strategy, dataA.strategyDetails]);

  // Strategy options based on methodological choice
  const strategyOptions = {
    quantitative: [
      {
        value: 'experiment',
        label: 'Experimental Design',
        description: 'Manipulate variables in controlled conditions to establish causality',
        bestFor: 'Testing causal hypotheses, high internal validity required',
        requirements: 'Control group, random assignment, manipulation of IV',
        glossaryKey: 'experiment'
      },
      {
        value: 'survey',
        label: 'Survey Research',
        description: 'Collect standardized data from large samples using questionnaires',
        bestFor: 'Describing populations, testing associations, efficiency needed',
        requirements: 'Representative sampling, validated instruments',
        glossaryKey: 'survey'
      },
      {
        value: 'archival',
        label: 'Archival/Secondary Data Analysis',
        description: 'Analyze existing datasets or records',
        bestFor: 'Longitudinal trends, cost-effective, historical analysis',
        requirements: 'Access to quality data sources',
        glossaryKey: 'archivalResearch'
      },
      {
        value: 'quasi-experiment',
        label: 'Quasi-Experimental Design',
        description: 'Compare groups without random assignment',
        bestFor: 'Field settings where random assignment is not feasible',
        requirements: 'Comparison groups, careful control of confounds'
      }
    ],
    qualitative: [
      {
        value: 'ethnography',
        label: 'Ethnography',
        description: 'Immersive study of culture and social practices in natural settings',
        bestFor: 'Understanding cultural contexts, social meanings, group practices',
        requirements: 'Extended fieldwork, participant observation, cultural sensitivity',
        glossaryKey: 'ethnography'
      },
      {
        value: 'groundedTheory',
        label: 'Grounded Theory',
        description: 'Generate theory from systematic analysis of data',
        bestFor: 'Theory development, process understanding, new phenomena',
        requirements: 'Iterative data collection, theoretical sampling, constant comparison',
        glossaryKey: 'groundedTheory'
      },
      {
        value: 'phenomenology',
        label: 'Phenomenology',
        description: 'Explore the lived experience and essence of a phenomenon',
        bestFor: 'Understanding subjective experience, meaning-making',
        requirements: 'Bracketing, phenomenological reduction, deep interviewing',
        glossaryKey: 'phenomenology'
      },
      {
        value: 'caseStudy',
        label: 'Case Study',
        description: 'In-depth investigation of single or multiple cases',
        bestFor: 'Complex phenomena in real contexts, "how" and "why" questions',
        requirements: 'Multiple data sources, triangulation, rich context',
        glossaryKey: 'caseStudy'
      },
      {
        value: 'narrative',
        label: 'Narrative Research',
        description: 'Collect and analyze stories of individual experiences',
        bestFor: 'Understanding life trajectories, identity, personal meaning',
        requirements: 'Story collection, narrative analysis skills',
        glossaryKey: 'narrativeResearch'
      },
      {
        value: 'actionResearch',
        label: 'Action Research',
        description: 'Collaborative problem-solving with practitioners',
        bestFor: 'Practical change, empowerment, iterative improvement',
        requirements: 'Practitioner collaboration, iterative cycles, reflection',
        glossaryKey: 'actionResearch'
      },
      {
        value: 'contentAnalysis',
        label: 'Content/Document Analysis',
        description: 'Systematic analysis of texts and documents',
        bestFor: 'Analyzing written materials, media, historical documents',
        requirements: 'Access to documents, coding framework'
      },
      {
        value: 'discourseAnalysis',
        label: 'Discourse Analysis',
        description: 'Analyze language use and meaning construction',
        bestFor: 'Understanding power, identity, social construction through language',
        requirements: 'Linguistic skills, theoretical grounding'
      }
    ],
    mixed: [
      {
        value: 'convergent',
        label: 'Convergent Parallel Design',
        description: 'Collect QUAL and QUANT data simultaneously, merge findings',
        bestFor: 'Corroborating findings, developing comprehensive understanding',
        requirements: 'Integration plan, expertise in both methods'
      },
      {
        value: 'explanatory',
        label: 'Explanatory Sequential Design',
        description: 'QUANT → QUAL: Quantitative followed by qualitative to explain',
        bestFor: 'Explaining quantitative results, exploring unexpected findings',
        requirements: 'Two-phase data collection, integration points'
      },
      {
        value: 'exploratory',
        label: 'Exploratory Sequential Design',
        description: 'QUAL → QUANT: Qualitative followed by quantitative to test/generalize',
        bestFor: 'Developing instruments, testing emergent theories',
        requirements: 'Two-phase design, instrument development phase'
      },
      {
        value: 'embedded',
        label: 'Embedded Design',
        description: 'Nest one method within a larger design of the other',
        bestFor: 'Supporting a primary method with complementary data',
        requirements: 'Clear primary method, specified role of embedded data'
      },
      {
        value: 'transformative',
        label: 'Transformative Design',
        description: 'Use mixed methods to address social justice and equity',
        bestFor: 'Advocacy, empowerment, understanding and addressing inequities',
        requirements: 'Social justice framework, community engagement'
      },
      {
        value: 'multiphase',
        label: 'Multiphase Design',
        description: 'Multiple connected studies over time',
        bestFor: 'Long-term programs, complex interventions',
        requirements: 'Sustained resources, programmatic thinking'
      }
    ]
  };

  // Get relevant strategies based on methodological choice
  const getRelevantStrategies = () => {
    const method = dataA.methodologicalChoice;
    if (method === 'qualitative') return strategyOptions.qualitative;
    if (method === 'quantitative') return strategyOptions.quantitative;
    if (method === 'mixedMethods' || method === 'mixed') return strategyOptions.mixed;
    if (method === 'multiMethod') {
      // For multi-method, show both qual and quant options
      return [...strategyOptions.qualitative, ...strategyOptions.quantitative];
    }
    // If no methodology selected, show all available strategies
    return [...strategyOptions.quantitative, ...strategyOptions.qualitative, ...strategyOptions.mixed];
  };

  const relevantStrategies = getRelevantStrategies();

  // Validate strategy selection
  useEffect(() => {
    const newWarnings = [];
    
    // Case Study granularity check
    if (selectedStrategy === 'caseStudy' && !strategyDetails.caseType) {
      newWarnings.push({
        type: 'warning',
        message: 'Case studies should specify the type: single instrumental, multiple collective, or intrinsic case study.'
      });
    }

    // Grounded Theory variant check
    if (selectedStrategy === 'groundedTheory') {
      if (!strategyDetails.gtVariant) {
        newWarnings.push({
          type: 'warning',
          message: 'Specify Grounded Theory approach: Classic (Glaser), Straussian, or Constructivist (Charmaz)?'
        });
      }
      
      // Check alignment with theory approach
      if (dataA.theoryApproach === 'deductive') {
        newWarnings.push({
          type: 'error',
          message: 'Grounded Theory is incompatible with deductive approach. GT requires inductive theory generation from data.'
        });
      }
    }

    // Phenomenology variant check
    if (selectedStrategy === 'phenomenology' && !strategyDetails.phenomVariant) {
      newWarnings.push({
        type: 'warning',
        message: 'Specify phenomenological tradition: Transcendental (Husserl), Hermeneutic (Heidegger), or Interpretive (van Manen)?'
      });
    }

    // Experiment requirements
    if (selectedStrategy === 'experiment') {
      if (!strategyDetails.hasControlGroup) {
        newWarnings.push({
          type: 'error',
          message: 'Experimental designs require a control or comparison group.'
        });
      }
      if (!strategyDetails.randomAssignment) {
        newWarnings.push({
          type: 'error',
          message: 'True experiments require random assignment to conditions. Without it, use quasi-experimental design.'
        });
      }
    }

    // Philosophy alignment checks
    if (dataA.philosophy === 'positivism' && strategyOptions.qualitative.find(s => s.value === selectedStrategy)) {
      newWarnings.push({
        type: 'warning',
        message: 'Positivist philosophy typically aligns with quantitative strategies. Your combination is unconventional but possible if justified.'
      });
    }

    if (dataA.philosophy === 'interpretivism' && strategyOptions.quantitative.find(s => s.value === selectedStrategy)) {
      newWarnings.push({
        type: 'warning',
        message: 'Interpretivist philosophy typically aligns with qualitative strategies. Your combination requires strong justification.'
      });
    }

    setWarnings(newWarnings);
  }, [selectedStrategy, strategyDetails, dataA.philosophy, dataA.theoryApproach]);

  const handleStrategyChange = (value) => {
    setSelectedStrategy(value);
    setStrategyDetails({}); // Reset details when changing strategy
  };

  const handleDetailsChange = (key, value) => {
    setStrategyDetails(prev => ({ ...prev, [key]: value }));
  };

  const canProceed = () => {
    if (!selectedStrategy) return false;
    
    // Block if critical errors exist
    const hasBlockingErrors = warnings.some(w => w.type === 'error');
    if (hasBlockingErrors) return false;

    // Additional requirements based on strategy
    if (selectedStrategy === 'caseStudy' && !strategyDetails.caseCount) return false;
    if (selectedStrategy === 'experiment' && (!strategyDetails.hasControlGroup || !strategyDetails.randomAssignment)) return false;
    
    return true;
  };

  const handleNext = () => {
    updateDataRaw({
      'wizardA.strategy': selectedStrategy,
      'wizardA.strategyDetails': strategyDetails
    });

    // Search across ALL strategy options to find the selected one for logging
    const allStrategies = [
      ...strategyOptions.quantitative,
      ...strategyOptions.qualitative,
      ...strategyOptions.mixed
    ];
    const selectedOption = allStrategies.find(s => s.value === selectedStrategy);
    
    const strategyLabel = selectedOption?.label || selectedStrategy || 'Not selected';
    const bestFor = selectedOption?.bestFor || 'See strategy details';
    const detailsStr = Object.entries(strategyDetails).length > 0 
      ? Object.entries(strategyDetails).map(([k, v]) => `${k}: ${v}`).join(', ')
      : '';
    
    logDecision(
      'Strategy Selection',
      strategyLabel,
      `Selected ${strategyLabel}. Best for: ${bestFor}. ${detailsStr}`.trim()
    );

    nextStep();
  };

  const selectedStrategyInfo = relevantStrategies.find(s => s.value === selectedStrategy);

  return (
    <div className="wizard-step">
      <h2>Step 6: Research Strategy</h2>
      <p className="step-description">
        Select the overarching research strategy that will guide your study design and execution.
      </p>

      <ExplanationBox title="What is a Research Strategy?">
        <p>
          Research strategies are the overall approaches you'll use to collect and analyze data. 
          Your choice is guided by your research questions, philosophical stance, and methodological choice.
        </p>
        <p><strong>Current context:</strong></p>
        <ul>
          <li>Philosophy: {dataA.philosophy || 'Not selected'}</li>
          <li>Theory Approach: {dataA.theoryApproach || 'Not selected'}</li>
          <li>Methodological Choice: {dataA.methodologicalChoice || 'Not selected'}</li>
        </ul>
      </ExplanationBox>

      <div className="form-section">
        <h3>Available Strategies</h3>
        <p className="helper-text">
          Based on your {dataA.methodologicalChoice} approach, these strategies are most appropriate:
        </p>

        <div className="strategy-grid">
          {relevantStrategies.map(strategy => (
            <div
              key={strategy.value}
              className={`strategy-card ${selectedStrategy === strategy.value ? 'selected' : ''}`}
              onClick={() => handleStrategyChange(strategy.value)}
            >
              <div className="strategy-header">
                <input
                  type="radio"
                  name="strategy"
                  value={strategy.value}
                  checked={selectedStrategy === strategy.value}
                  onChange={(e) => handleStrategyChange(e.target.value)}
                />
                <strong>{strategy.label}</strong>
                {strategy.glossaryKey && <GlossaryTerm termKey={strategy.glossaryKey} />}
              </div>
              <p className="strategy-description">{strategy.description}</p>
              <div className="strategy-meta">
                <small><strong>Best for:</strong> {strategy.bestFor}</small>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Strategy-specific details */}
      {selectedStrategy && (
        <div className="form-section">
          <h3>Strategy Details</h3>
          
          {selectedStrategy === 'caseStudy' && (
            <>
              <div className="form-group">
                <label>Number of Cases</label>
                <input
                  type="number"
                  min="1"
                  value={strategyDetails.caseCount || ''}
                  onChange={(e) => handleDetailsChange('caseCount', e.target.value)}
                  placeholder="e.g., 1, 3, 5"
                />
                <small>Single case for depth, multiple cases for replication</small>
              </div>
              <div className="form-group">
                <label>Case Study Type</label>
                <select
                  value={strategyDetails.caseType || ''}
                  onChange={(e) => handleDetailsChange('caseType', e.target.value)}
                >
                  <option value="">Select type...</option>
                  <option value="instrumental">Instrumental (case illustrates an issue)</option>
                  <option value="intrinsic">Intrinsic (case itself is of interest)</option>
                  <option value="collective">Collective/Multiple (compare/contrast cases)</option>
                </select>
              </div>
            </>
          )}

          {selectedStrategy === 'groundedTheory' && (
            <div className="form-group">
              <label>Grounded Theory Variant</label>
              <select
                value={strategyDetails.gtVariant || ''}
                onChange={(e) => handleDetailsChange('gtVariant', e.target.value)}
              >
                <option value="">Select variant...</option>
                <option value="classic">Classic (Glaser) - Emergence, minimal preconceptions</option>
                <option value="straussian">Straussian (Strauss & Corbin) - Systematic coding paradigm</option>
                <option value="constructivist">Constructivist (Charmaz) - Social construction, reflexivity</option>
              </select>
            </div>
          )}

          {selectedStrategy === 'phenomenology' && (
            <div className="form-group">
              <label>Phenomenological Tradition</label>
              <select
                value={strategyDetails.phenomVariant || ''}
                onChange={(e) => handleDetailsChange('phenomVariant', e.target.value)}
              >
                <option value="">Select tradition...</option>
                <option value="transcendental">Transcendental (Husserl) - Essence, bracketing, eidetic reduction</option>
                <option value="hermeneutic">Hermeneutic (Heidegger) - Interpretation, being-in-the-world</option>
                <option value="interpretive">Interpretive (van Manen) - Lived experience texts</option>
              </select>
            </div>
          )}

          {selectedStrategy === 'experiment' && (
            <>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={strategyDetails.randomAssignment || false}
                    onChange={(e) => handleDetailsChange('randomAssignment', e.target.checked)}
                  />
                  Random assignment to conditions
                </label>
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={strategyDetails.hasControlGroup || false}
                    onChange={(e) => handleDetailsChange('hasControlGroup', e.target.checked)}
                  />
                  Control or comparison group
                </label>
              </div>
              <div className="form-group">
                <label>Experimental Design Type</label>
                <select
                  value={strategyDetails.expType || ''}
                  onChange={(e) => handleDetailsChange('expType', e.target.value)}
                >
                  <option value="">Select type...</option>
                  <option value="between">Between-subjects (independent groups)</option>
                  <option value="within">Within-subjects (repeated measures)</option>
                  <option value="mixed">Mixed design (both between and within factors)</option>
                  <option value="factorial">Factorial (multiple IVs)</option>
                </select>
              </div>
            </>
          )}

          {selectedStrategy === 'ethnography' && (
            <div className="form-group">
              <label>Estimated Fieldwork Duration</label>
              <input
                type="text"
                value={strategyDetails.fieldworkDuration || ''}
                onChange={(e) => handleDetailsChange('fieldworkDuration', e.target.value)}
                placeholder="e.g., 6 months, 1 year"
              />
              <small>Ethnography typically requires extended immersion (months to years)</small>
            </div>
          )}
        </div>
      )}

      {/* Warnings and Guardrails */}
      {warnings.length > 0 && (
        <div className="warnings-section">
          {warnings.map((warning, idx) => (
            <GuardrailWarning key={idx} type={warning.type} title={warning.type === 'error' ? 'Critical Issue' : 'Recommendation'}>
              {warning.message}
            </GuardrailWarning>
          ))}
        </div>
      )}

      {selectedStrategyInfo && (
        <ExplanationBox title="Why this strategy?">
          <p><strong>Requirements:</strong> {selectedStrategyInfo.requirements}</p>
          <p><strong>Alignment:</strong> This strategy fits your {dataA.methodologicalChoice} approach and {dataA.philosophy} philosophy.</p>
        </ExplanationBox>
      )}

      <NavigationButtons
        onNext={handleNext}
        canProceed={true}
        nextLabel="Continue to Time Horizon"
      />
    </div>
  );
};

export default Step4_Strategy;
